import {
  CSSProperties,
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from 'react';
import {
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  Grid,
  FormControlLabel,
  Checkbox,
  Box,
  Button,
  TextField,
  Switch,
  Tooltip,
  Typography,
} from '@material-ui/core';
import { Autocomplete, createFilterOptions } from '@material-ui/lab';
import { useApi } from '@backstage/core-plugin-api';
import { useSearchParams } from 'react-router-dom';
import { securityInsightsApiRef } from '../../api/apiRef';
import {
  TaxonomyCategory,
  TaxonomyPillar,
  UNCLASSIFIED_TAXONOMY,
} from '../../api/types';
import { formatToolName } from '../../utils/textFormatting';

const SEVERITY_ORDER = [
  'CRITICAL',
  'HIGH',
  'MEDIUM',
  'LOW',
  'INFORMATIONAL',
  'UNKNOWN',
  'NONE',
];

const SEVERITY_RANK = new Map(SEVERITY_ORDER.map((s, i) => [s, i]));

/** Sort severities by canonical importance order (critical first). */
function sortSeverities(sevs: string[]): string[] {
  return [...sevs].sort(
    (a, b) => (SEVERITY_RANK.get(a) ?? 99) - (SEVERITY_RANK.get(b) ?? 99),
  );
}

const SEVERITY_COLORS: Record<string, string> = {
  CRITICAL: '#d32f2f',
  HIGH: '#f57c00',
  MEDIUM: '#fbc02d',
  LOW: '#1976d2',
  INFORMATIONAL: '#9e9e9e',
  UNKNOWN: '#757575',
  NONE: '#bdbdbd',
};

const DEFAULT_SEVERITIES = ['CRITICAL', 'HIGH'];

// ── System / Deployed Instance: one control, two levels ────────────────────
// Like Pillar / Category, but a searchable Autocomplete rather than a Select:
// the catalog has hundreds of Systems. Systems are the bold rows with their
// registered instances indented beneath. The selection still lives in TWO
// independent states, URL params (`system`, `instance`) and API fields, so
// every deep link and the backend contract are untouched; the combined control
// is presentation only. Picking an instance always sets its System too: an
// instance name alone is ambiguous (the same name can be registered under more
// than one System, and a System with instances also has an `Unattributed` one),
// so it never stands without the System it was picked under.
//
// A System with no registered instances is just its own row. Picking the System
// already selects all of its findings, so an `Unattributed` instance beneath it
// would be the same set under a second name.
const ATTRIBUTION_UNATTRIBUTED = 'Unattributed';
// Unit separator: keeps `system` and `instance` apart in an option's value.
const OPTION_SEPARATOR = '\u001f';

interface SystemInstanceOption {
  /** 'All' | `s:<system>` | `i:<system><sep><instance>` — unique per option. */
  value: string;
  /** What the input shows once chosen, so it is unambiguous on its own. */
  label: string;
  /** What the list row shows; an instance's System is implied by its position. */
  name: string;
  level: 'all' | 'system' | 'instance';
  system: string;
  instance: string;
}

const ALL_SYSTEMS_OPTION: SystemInstanceOption = {
  value: 'All',
  label: 'All',
  name: 'All',
  level: 'all',
  system: 'All',
  instance: 'All',
};

// Typing matches the label shown once chosen ("payments / payments-prod"), so
// "payments" finds the System and every instance under it, and "payments-prod"
// finds just that instance. Module-level: it needs no component state.
const filterSystemInstanceOptions = createFilterOptions<SystemInstanceOption>({
  stringify: o => o.label,
});

/** How a row looks in the list: Systems bold, their instances indented. */
function systemInstanceRowStyle(
  level: SystemInstanceOption['level'],
): CSSProperties | undefined {
  if (level === 'instance') return { paddingLeft: 24 };
  if (level === 'system') return { fontWeight: 600 };
  return undefined;
}

/** The option standing for a (system, instance) selection, registered or not. */
function systemInstanceOptionFor(
  system: string,
  instance: string,
): SystemInstanceOption {
  if (system === 'All' && instance === 'All') return ALL_SYSTEMS_OPTION;
  if (instance === 'All') {
    return {
      value: `s:${system}`,
      label: system,
      name: system,
      level: 'system',
      system,
      instance,
    };
  }
  // An instance with no System (a hand-edited or older link): kept honest
  // rather than guessed at, since the same name may belong to several.
  const label =
    system === 'All' ? `${instance} (any System)` : `${system} / ${instance}`;
  return {
    value: `i:${system === 'All' ? '' : system}${OPTION_SEPARATOR}${instance}`,
    label,
    name: system === 'All' ? label : instance,
    level: 'instance',
    system,
    instance,
  };
}

/**
 * 'All', then `Unattributed` (findings with no System) and every System. A
 * System with registered instances is followed by them, an `Unattributed` one
 * first (the System's own findings that sit on no deployed instance —
 * repo-level findings); a System with none is just its own row. Only registered
 * instances are listed; an instance whose tag Backstage doesn't know has no
 * System to sit under here.
 */
function buildSystemInstanceOptions(
  systems: string[],
  instancesBySystem: Record<string, string[]>,
): SystemInstanceOption[] {
  // A registered System literally named like the bucket must not list twice.
  const allSystems = [
    ...new Set([...systems, ...Object.keys(instancesBySystem)]),
  ].filter(system => system !== ATTRIBUTION_UNATTRIBUTED);
  const options: SystemInstanceOption[] = [ALL_SYSTEMS_OPTION];
  for (const system of [ATTRIBUTION_UNATTRIBUTED, ...allSystems]) {
    options.push(systemInstanceOptionFor(system, 'All'));
    const instances = (instancesBySystem[system] ?? []).filter(
      instance => instance !== ATTRIBUTION_UNATTRIBUTED,
    );
    if (instances.length === 0) continue;
    options.push(systemInstanceOptionFor(system, ATTRIBUTION_UNATTRIBUTED));
    for (const instance of instances) {
      options.push(systemInstanceOptionFor(system, instance));
    }
  }
  return options;
}

interface SingleViewFilterPanelProps {
  onFiltersChange: (filters: {
    selectedExperience: string;
    selectedTeam: string;
    /** Present only when `showSystemFilters` is set. */
    selectedSystem?: string;
    selectedDeployedInstance?: string;
    selectedSeverities: string[];
    selectedIssueType: string;
    selectedPillar: string;
    selectedCategory: string;
    selectedCve: string;
    selectedSloCheck: boolean;
    deduplicate: boolean;
    selectedSubTeamSearch: string;
    includeQuiesced: boolean;
  }) => void;
  searchLabel?: string;
  searchPlaceholder?: string;
  searchParamKey?: string;
  lockedTeam?: string;
  /** Opt-in System / Deployed Instance control (one combined control). Only the surfaces whose
   *  backend endpoints honour `selectedSystem`/`selectedDeployedInstance`
   *  should set this: when false (the default) the panel fetches no System
   *  options, ignores the `system`/`instance` URL params, and leaves both keys
   *  out of `onFiltersChange` — so a control can never promise a scope the
   *  page's queries then silently ignore. */
  showSystemFilters?: boolean;
  /** Names of the locked group's direct child teams only (not the full
   *  transitive descendant closure), used to populate the "Sub-team"
   *  dropdown. Gates the dropdown's rendering, URL persistence, and
   *  at-default check — ignored when `lockedTeam` is not set.
   *  Empty/undefined means the group has no direct sub-teams. */
  subTeamOptions?: string[];
  /** Fired whenever the at-default state of the filters changes, so a parent
   *  can render an enabled/disabled "Clear all filters" button alongside its
   *  own page header without owning the filter state itself. */
  onAtDefaultChange?: (atDefault: boolean) => void;
  /** Query-param key used to persist the sub-team search in the URL. */
  subTeamSearchParamKey?: string;
  /** Hides the Pillar/Category dropdown. Every page's query goes through
   *  `buildBaseSingleViewVars`, which now applies `selected_pillar`/
   *  `selected_category` even on pages already scoped to one pillar (e.g.
   *  License Compliance) — picking a different pillar there would AND a
   *  contradictory filter onto an already-scoped query and silently return
   *  zero rows. Defaults to shown. */
  showTaxonomyFilter?: boolean;
  /**
   * Purely visual override for the Team/Experience/Pillar/Category/Issue
   * Type controls below — lets a caller with its own separate drill-down
   * scope (e.g. SecurityPostureReportPage's matrix/issues-table drill,
   * which is deliberately NOT part of `onFiltersChange` so it doesn't also
   * narrow the matrix/donut/group-by data) make these controls visibly
   * agree with whatever the caller's own results are actually showing.
   * Only changes what's *displayed*; `onFiltersChange`, the URL sync, and
   * the at-default check all keep using this panel's own real selection,
   * unaffected by the override. Omit a field (or the whole prop) to fall
   * back to the real selection for that control.
   */
  displayOverride?: {
    team?: string;
    experience?: string;
    pillar?: string;
    category?: string;
    issueType?: string;
  };
}

/**
 * A `displayOverride` value that wins over the panel's own selection, but only
 * until the user edits that control themselves.
 *
 * Without the dismissal, every render would snap the control straight back to
 * the override's value, so picking anything else in it would look broken. And
 * without re-arming, a dismissal would be permanent — a *fresh* drill click
 * (a genuinely different override value) has to win again.
 *
 * Both transitions are handled by comparing against the previous signature
 * held in state, during render — React's supported "adjust state when a prop
 * changes" pattern, not an effect, which would show the stale value for one
 * extra render. See
 * https://react.dev/reference/react/useState#storing-information-from-previous-renders
 *
 * @param signature - A value that changes whenever the override moves to a
 *   genuinely different target. `undefined` means "no override".
 * @returns `active` — whether the override should currently be displayed —
 *   and `dismiss`, to call from the control's own change/clear handlers.
 */
function useDismissibleOverride(signature: string | undefined) {
  const [lastSignature, setLastSignature] = useState(signature);
  const [dismissed, setDismissed] = useState(false);
  if (signature !== lastSignature) {
    setLastSignature(signature);
    setDismissed(false);
  }
  return {
    active: !dismissed,
    dismiss: useCallback(() => setDismissed(true), []),
  };
}

/** Imperative handle exposed via ref so a parent-rendered Clear button can
 *  reset filters back to defaults without lifting the filter state out of
 *  this component. Paired with `onAtDefaultChange` for the disabled state. */
export interface SingleViewFilterPanelHandle {
  clearAll: () => void;
}

export const SingleViewFilterPanel = forwardRef<
  SingleViewFilterPanelHandle,
  SingleViewFilterPanelProps
>(
  (
    {
      onFiltersChange,
      searchLabel = 'Search by CVE / Rule',
      searchPlaceholder = 'e.g. CVE-2024-1234',
      searchParamKey = 'cve',
      lockedTeam,
      showSystemFilters = false,
      subTeamOptions,
      onAtDefaultChange,
      subTeamSearchParamKey = 'subTeam',
      showTaxonomyFilter = true,
      displayOverride,
    }: SingleViewFilterPanelProps,
    ref,
  ) => {
    const api = useApi(securityInsightsApiRef);
    const [searchParams, setSearchParams] = useSearchParams();
    const initialized = useRef(false);
    const subTeamSearchEnabled =
      Boolean(lockedTeam) && (subTeamOptions?.length ?? 0) > 0;

    const [experiences, setExperiences] = useState<string[]>([]);
    const [teams, setTeams] = useState<string[]>([]);
    const [teamsMap, setTeamsMap] = useState<Record<string, string[]>>({});
    const [systems, setSystems] = useState<string[]>([]);
    const [instancesBySystem, setInstancesBySystem] = useState<
      Record<string, string[]>
    >({});
    const [severities, setSeverities] = useState<string[]>([]);
    const [issueTypes, setIssueTypes] = useState<string[]>([]);
    const [pillars, setPillars] = useState<TaxonomyPillar[]>([]);
    const [categories, setCategories] = useState<TaxonomyCategory[]>([]);

    const [selectedExperience, setSelectedExperience] = useState(
      lockedTeam ? 'All' : searchParams.get('experience') || 'All',
    );
    const [selectedTeam, setSelectedTeam] = useState(
      lockedTeam || searchParams.get('team') || 'All',
    );
    const [selectedSystem, setSelectedSystem] = useState(
      (showSystemFilters && searchParams.get('system')) || 'All',
    );
    const [selectedDeployedInstance, setSelectedDeployedInstance] = useState(
      (showSystemFilters && searchParams.get('instance')) || 'All',
    );
    const [selectedSeverities, setSelectedSeverities] = useState<string[]>(
      searchParams.get('severities')
        ? sortSeverities([
            ...new Set(searchParams.get('severities')!.split(',')),
          ])
        : DEFAULT_SEVERITIES,
    );
    const [selectedIssueType, setSelectedIssueType] = useState(
      searchParams.get('issueType') || 'All',
    );
    const [selectedPillar, setSelectedPillar] = useState(
      searchParams.get('pillar') || 'All',
    );
    const [selectedCategory, setSelectedCategory] = useState(
      searchParams.get('category') || 'All',
    );
    const [selectedCve, setSelectedCve] = useState(
      searchParams.get(searchParamKey) || '',
    );
    const [selectedSubTeamSearch, setSelectedSubTeamSearch] = useState(
      subTeamSearchEnabled ? searchParams.get(subTeamSearchParamKey) || '' : '',
    );
    const [selectedSloCheck, setSelectedSloCheck] = useState(
      searchParams.get('sloCheck') === 'true',
    );
    const [deduplicate, setDeduplicate] = useState(
      searchParams.get('isUnique') === 'true',
    );
    const [includeQuiesced, setIncludeQuiesced] = useState(
      searchParams.get('isQuiesced') === 'true',
    );

    // Captured once, before any effect can strip it from the URL. If
    // `subTeamOptions` is empty at mount (e.g. entity relations not yet
    // loaded) and later becomes non-empty, the state below must still be able
    // to recover a deep-linked `?subTeam=` value that the URL-sync effect
    // already removed while the dropdown was hidden.
    const initialSubTeamSearchFromUrl = useRef(
      searchParams.get(subTeamSearchParamKey) || '',
    );

    // Content-based (not reference-based) dependency: `subTeamOptions` may be a
    // fresh array literal on every render even when its contents haven't
    // changed, and keying the effect below off the array reference would rerun
    // it — and therefore re-evaluate the selection — on every render.
    const subTeamOptionsKey = (subTeamOptions ?? []).join(' ');

    // A group can lose its sub-teams between renders (e.g. entity refetch), so
    // the dropdown must not keep a stale selection it can no longer show. Also
    // re-validates whenever `subTeamOptions` changes contents while staying
    // non-empty (e.g. the selected sub-team is renamed/removed but the group
    // still has others) — not just when the dropdown's enabled/disabled state
    // flips — since MUI Autocomplete warns (and can misbehave) when given a
    // `value` absent from `options`. Empty/whitespace-only is left as-is
    // rather than force-cleared: it already means "All sub-teams", not an
    // invalid selection. And when sub-teams appear after mount, restore the
    // original URL value rather than leaving the selection empty (see
    // `initialSubTeamSearchFromUrl` above).
    useEffect(() => {
      if (!subTeamSearchEnabled) {
        setSelectedSubTeamSearch('');
        return;
      }
      setSelectedSubTeamSearch(prev => {
        const candidate = prev || initialSubTeamSearchFromUrl.current;
        const trimmed = candidate.trim();
        if (!trimmed) return candidate;
        const stillValid = (subTeamOptions ?? []).some(
          o => o.toLowerCase() === trimmed.toLowerCase(),
        );
        return stillValid ? candidate : '';
      });
      // subTeamOptions is intentionally excluded: subTeamOptionsKey (its content,
      // not its reference) is the real dependency — see comment above.
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [subTeamSearchEnabled, subTeamOptionsKey]);

    useEffect(() => {
      Promise.all([
        api.getExperiences(),
        api.getTeams(),
        api.getTeamsInExperiences(),
        api.getSeverities(),
        api.getIssueTypes(),
        // The taxonomy vocabulary is served from reference tables that require
        // their own Postgres GRANT. Degrade to an empty list (which hides the
        // two dropdowns) rather than rejecting the whole Promise.all and
        // leaving every filter unpopulated.
        api.getPillars().catch(() => [] as TaxonomyPillar[]),
        api.getCategories().catch(() => [] as TaxonomyCategory[]),
        // System attribution comes from its own registry, so a failure there
        // must leave the two dropdowns empty rather than take down the rest.
        // Skipped entirely unless the caller opted in.
        showSystemFilters
          ? api.getSystems().catch(() => [] as string[])
          : Promise.resolve([] as string[]),
        showSystemFilters
          ? api
              .getDeployedInstancesBySystem()
              .catch(() => ({}) as Record<string, string[]>)
          : Promise.resolve({} as Record<string, string[]>),
      ]).then(([exps, tms, tMap, sevs, types, plrs, cats, syss, instMap]) => {
        setExperiences(exps);
        setTeams(tms);
        setTeamsMap(tMap);
        setSystems(syss);
        setInstancesBySystem(instMap);
        setSeverities(sevs);
        setPillars(plrs);
        setCategories(cats);
        // Only apply default severities if URL didn't specify them;
        // validate any URL-supplied severities against the API allowlist
        const urlSeverities = searchParams.get('severities');
        if (!urlSeverities) {
          setSelectedSeverities(
            sortSeverities(sevs.filter(s => DEFAULT_SEVERITIES.includes(s))),
          );
        } else {
          const allowed = new Set(sevs);
          setSelectedSeverities(
            sortSeverities(
              urlSeverities.split(',').filter(s => allowed.has(s)),
            ),
          );
        }
        setIssueTypes(types);
        initialized.current = true;
      });
      // searchParams is intentionally excluded: options are fetched once on mount.
      // URL params are read via the searchParams captured in the initial render.
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [api]);

    // Sync filters to URL (only non-default values)
    useEffect(() => {
      if (!initialized.current) return;
      setSearchParams(
        prev => {
          const params = new URLSearchParams(prev);
          // Manage only filter-owned keys; preserve other params (e.g. view)
          const filterKeys = [
            'experience',
            'team',
            'issueType',
            'pillar',
            'category',
            searchParamKey,
            subTeamSearchParamKey,
            'sloCheck',
            'severities',
            'isUnique',
            'isQuiesced',
          ];
          // Leave `system`/`instance` untouched unless this panel owns them.
          if (showSystemFilters) filterKeys.push('system', 'instance');
          filterKeys.forEach(k => params.delete(k));
          if (!lockedTeam && selectedExperience !== 'All')
            params.set('experience', selectedExperience);
          if (!lockedTeam && selectedTeam !== 'All')
            params.set('team', selectedTeam);
          if (showSystemFilters && selectedSystem !== 'All')
            params.set('system', selectedSystem);
          if (showSystemFilters && selectedDeployedInstance !== 'All')
            params.set('instance', selectedDeployedInstance);
          if (selectedIssueType !== 'All')
            params.set('issueType', selectedIssueType);
          if (selectedPillar !== 'All') params.set('pillar', selectedPillar);
          if (selectedCategory !== 'All')
            params.set('category', selectedCategory);
          if (selectedCve) params.set(searchParamKey, selectedCve);
          if (subTeamSearchEnabled && selectedSubTeamSearch.trim())
            params.set(subTeamSearchParamKey, selectedSubTeamSearch.trim());
          if (selectedSloCheck) params.set('sloCheck', 'true');
          if (deduplicate) params.set('isUnique', 'true');
          if (includeQuiesced) params.set('isQuiesced', 'true');
          const defaultSevs = severities.filter(s =>
            DEFAULT_SEVERITIES.includes(s),
          );
          const sevsAreDifferent =
            selectedSeverities.length !== defaultSevs.length ||
            !selectedSeverities.every(s => defaultSevs.includes(s));
          if (sevsAreDifferent)
            params.set('severities', selectedSeverities.join(','));
          return params;
        },
        { replace: true },
      );
    }, [
      selectedExperience,
      selectedTeam,
      showSystemFilters,
      selectedSystem,
      selectedDeployedInstance,
      selectedSeverities,
      selectedIssueType,
      selectedPillar,
      selectedCategory,
      selectedCve,
      selectedSubTeamSearch,
      subTeamSearchEnabled,
      selectedSloCheck,
      deduplicate,
      includeQuiesced,
      severities,
      setSearchParams,
      searchParamKey,
      subTeamSearchParamKey,
      lockedTeam,
    ]);

    // Debounce filter changes so rapid clicks (e.g. toggling several severity
    // chips in quick succession, or typing in the CVE search box) coalesce into
    // a single downstream fetch instead of one fetch per keystroke / click.
    useEffect(() => {
      const t = setTimeout(() => {
        onFiltersChange({
          selectedExperience,
          selectedTeam,
          ...(showSystemFilters
            ? { selectedSystem, selectedDeployedInstance }
            : {}),
          selectedSeverities,
          selectedIssueType,
          selectedPillar,
          selectedCategory,
          selectedCve,
          selectedSloCheck,
          deduplicate,
          selectedSubTeamSearch,
          includeQuiesced,
        });
      }, 300);
      return () => clearTimeout(t);
    }, [
      selectedExperience,
      selectedTeam,
      showSystemFilters,
      selectedSystem,
      selectedDeployedInstance,
      selectedSeverities,
      selectedIssueType,
      selectedPillar,
      selectedCategory,
      selectedCve,
      selectedSubTeamSearch,
      selectedSloCheck,
      deduplicate,
      includeQuiesced,
      onFiltersChange,
    ]);

    const availableTeams =
      selectedExperience !== 'All' && selectedExperience !== 'Unknown'
        ? teamsMap[selectedExperience] || []
        : teams;

    // See `SystemInstanceOption` for why this is one control over two states.
    //
    // Memoised, and the selected `value` below is looked up *from* the options,
    // because MUI's Autocomplete resets the input text whenever `value` changes
    // identity (no focus guard): a fresh object per render would wipe what
    // someone is typing the moment anything re-renders the panel (a results
    // fetch finishing, a URL update).
    const systemInstanceOptions = useMemo(
      () => buildSystemInstanceOptions(systems, instancesBySystem),
      [systems, instancesBySystem],
    );
    const selectedSystemInstanceOption = useMemo(() => {
      const wanted = systemInstanceOptionFor(
        selectedSystem,
        selectedDeployedInstance,
      );
      const registered = systemInstanceOptions.find(
        o => o.value === wanted.value,
      );
      if (registered) return registered;
      // The current selection must always be one of the options (MUI warns, and
      // shows nothing, otherwise): a deep link naming something unregistered, or
      // options that haven't loaded yet, would otherwise leave it dangling. It
      // has no System header above it, so it names itself in full.
      return { ...wanted, name: wanted.label, level: 'all' as const };
    }, [systemInstanceOptions, selectedSystem, selectedDeployedInstance]);
    const systemInstanceChoices = useMemo(
      () =>
        systemInstanceOptions.includes(selectedSystemInstanceOption)
          ? systemInstanceOptions
          : [
              systemInstanceOptions[0],
              selectedSystemInstanceOption,
              ...systemInstanceOptions.slice(1),
            ],
      [systemInstanceOptions, selectedSystemInstanceOption],
    );

    const handleSystemInstanceChange = (option: SystemInstanceOption) => {
      setSelectedSystem(option.system);
      setSelectedDeployedInstance(option.instance);
    };

    // Pillar and Category are one control with two levels, but stay TWO
    // independent URL params and two independent API fields. That keeps every
    // existing deep link and the backend contract untouched while the UI reads
    // as a single choice. Encoding: 'All' | UNCLASSIFIED | 'p:<pillar>' |
    // 'c:<category>'. Category codes are globally unique, so selecting one sets
    // `selectedCategory` alone — the pillar is implied by the row's own column
    // and adding a redundant pillar predicate would only make the URL noisier.
    const taxonomySelection = (() => {
      if (selectedCategory !== 'All') return `c:${selectedCategory}`;
      if (selectedPillar === UNCLASSIFIED_TAXONOMY)
        return UNCLASSIFIED_TAXONOMY;
      if (selectedPillar !== 'All') return `p:${selectedPillar}`;
      return 'All';
    })();

    // Display-only versions of the controls a caller can override (see
    // `displayOverride`'s doc comment above) — computed the same way as the
    // real selections, just sourced from the override when present.
    //
    // The override must win only until the user actually edits that same
    // control themselves — otherwise every render would snap the control
    // straight back to the override's value and picking anything else in it
    // would look broken/unresponsive. Tracked per independent control group
    // (Team+Experience share one pair, Pillar+Category share one Select,
    // Issue Type has its own) via a "dismissed" flag that's cleared again
    // as soon as the override moves on to a genuinely different value —
    // i.e. a fresh drill click, which should always win over a dismissal
    // from a previous one. Using React's supported "adjust state during
    // render when a prop changes" pattern (comparing against the previous
    // signature in state), not an effect — that would show the stale,
    // not-yet-adjusted value for one extra render.
    // One hook instance per independent control group: Team+Experience share
    // a pair (only one of them is ever overridden at a time), Pillar+Category
    // share a single Select, and Issue Type has its own.
    let teamOrExperienceOverrideSig: string | undefined;
    if (displayOverride?.team) {
      teamOrExperienceOverrideSig = `t:${displayOverride.team}`;
    } else if (displayOverride?.experience) {
      teamOrExperienceOverrideSig = `e:${displayOverride.experience}`;
    }
    const teamOverride = useDismissibleOverride(teamOrExperienceOverrideSig);

    let taxonomyOverrideSig: string | undefined;
    if (displayOverride?.category) {
      taxonomyOverrideSig = `c:${displayOverride.category}`;
    } else if (displayOverride?.pillar) {
      taxonomyOverrideSig = `p:${displayOverride.pillar}`;
    }
    const taxonomyOverride = useDismissibleOverride(taxonomyOverrideSig);

    const issueTypeOverride = useDismissibleOverride(
      displayOverride?.issueType,
    );

    const displayedTeam =
      teamOverride.active && displayOverride?.team
        ? displayOverride.team
        : selectedTeam;
    const displayedExperience =
      teamOverride.active && displayOverride?.experience
        ? displayOverride.experience
        : selectedExperience;
    const displayedTaxonomySelection = (() => {
      if (!taxonomyOverride.active) return taxonomySelection;
      if (displayOverride?.category) return `c:${displayOverride.category}`;
      if (displayOverride?.pillar === UNCLASSIFIED_TAXONOMY)
        return UNCLASSIFIED_TAXONOMY;
      if (displayOverride?.pillar) return `p:${displayOverride.pillar}`;
      return taxonomySelection;
    })();
    const displayedIssueType =
      issueTypeOverride.active && displayOverride?.issueType
        ? displayOverride.issueType
        : selectedIssueType;

    const handleTaxonomyChange = (value: string) => {
      if (value === 'All') {
        setSelectedPillar('All');
        setSelectedCategory('All');
      } else if (value === UNCLASSIFIED_TAXONOMY) {
        setSelectedPillar(UNCLASSIFIED_TAXONOMY);
        setSelectedCategory('All');
      } else if (value.startsWith('p:')) {
        setSelectedPillar(value.slice(2));
        setSelectedCategory('All');
      } else {
        setSelectedPillar('All');
        setSelectedCategory(value.slice(2));
      }
    };

    // A deep-linked code that isn't in the vocabulary (retired, or a link from a
    // newer taxonomy version) would leave the Select with a `value` matching no
    // MenuItem, which MUI warns about and renders blank. Reset to All once the
    // vocabulary has actually loaded — never before, or a valid bookmark gets
    // cleared during the fetch.
    useEffect(() => {
      if (pillars.length === 0) return;
      setSelectedPillar(prev =>
        prev === 'All' ||
        prev === UNCLASSIFIED_TAXONOMY ||
        pillars.some(p => p.code === prev)
          ? prev
          : 'All',
      );
    }, [pillars]);
    useEffect(() => {
      if (categories.length === 0) return;
      setSelectedCategory(prev =>
        prev === 'All' || categories.some(c => c.code === prev) ? prev : 'All',
      );
    }, [categories]);

    // A hand-edited or stale bookmark can carry a `pillar` param and a
    // `category` param from a *different* pillar (e.g.
    // `?pillar=vulnerability&category=identity_access`). Both would apply as
    // an AND, matching zero rows with no visible clue why — the Select only
    // ever displays the category in that state. Category is the more
    // specific of the two, so on load drop the mismatched pillar rather than
    // the category.
    useEffect(() => {
      if (categories.length === 0) return;
      if (selectedCategory === 'All' || selectedPillar === 'All') return;
      if (selectedPillar === UNCLASSIFIED_TAXONOMY) return;
      const category = categories.find(c => c.code === selectedCategory);
      if (category && category.pillar_code !== selectedPillar) {
        setSelectedPillar('All');
      }
      // selectedPillar is intentionally excluded: this effect only reacts to
      // the category/vocabulary settling, not to its own correction.
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [categories, selectedCategory]);

    const toggleSeverity = (sev: string) => {
      setSelectedSeverities(prev =>
        prev.includes(sev)
          ? prev.filter(s => s !== sev)
          : sortSeverities([...prev, sev]),
      );
    };

    // While the severities filter API call is still in flight, `severities` is
    // []. Falling back to DEFAULT_SEVERITIES in that window prevents the Clear
    // button from (a) appearing enabled on first render against an empty
    // baseline, and (b) wiping selectedSeverities to [] if the user clicks it
    // early — which the backend treats as an explicit empty allow-list rather
    // than "defaults", producing empty queries.
    const defaultSeveritiesFromApi =
      severities.length > 0
        ? sortSeverities(severities.filter(s => DEFAULT_SEVERITIES.includes(s)))
        : DEFAULT_SEVERITIES;

    // Returns true when every filter is already at its default value. Used to
    // disable the Clear button so it doesn't look interactive when there's
    // nothing to clear.
    const filtersAreAtDefault = () => {
      if (!lockedTeam) {
        if (selectedExperience !== 'All') return false;
        if (selectedTeam !== 'All') return false;
      }
      if (showSystemFilters) {
        if (selectedSystem !== 'All') return false;
        if (selectedDeployedInstance !== 'All') return false;
      }
      if (selectedIssueType !== 'All') return false;
      if (selectedPillar !== 'All') return false;
      if (selectedCategory !== 'All') return false;
      if (selectedCve) return false;
      if (subTeamSearchEnabled && selectedSubTeamSearch.trim()) return false;
      if (selectedSloCheck) return false;
      if (deduplicate) return false;
      if (includeQuiesced) return false;
      if (selectedSeverities.length !== defaultSeveritiesFromApi.length)
        return false;
      if (!selectedSeverities.every(s => defaultSeveritiesFromApi.includes(s)))
        return false;
      return true;
    };

    const handleClearFilters = () => {
      if (!lockedTeam) {
        setSelectedExperience('All');
        setSelectedTeam('All');
        teamOverride.dismiss();
      }
      if (showSystemFilters) {
        setSelectedSystem('All');
        setSelectedDeployedInstance('All');
      }
      setSelectedIssueType('All');
      issueTypeOverride.dismiss();
      setSelectedPillar('All');
      setSelectedCategory('All');
      taxonomyOverride.dismiss();
      setSelectedCve('');
      setSelectedSubTeamSearch('');
      setSelectedSloCheck(false);
      setDeduplicate(false);
      setIncludeQuiesced(false);
      setSelectedSeverities(defaultSeveritiesFromApi);
    };

    // Expose the clear action to a parent that wants to render the button in
    // its own ContentHeader (keeps Filters state local while letting the visual
    // affordance live in the page header row).
    useImperativeHandle(ref, () => ({ clearAll: handleClearFilters }));

    // Push the at-default boolean up so the parent can disable its Clear button
    // accurately without having to recompute the default state itself.
    const atDefault = filtersAreAtDefault();
    useEffect(() => {
      onAtDefaultChange?.(atDefault);
    }, [atDefault, onAtDefaultChange]);

    // Matches case-insensitively so a deep-linked `?subTeam=` value whose
    // casing differs from the option's real catalog-slug casing (e.g. an
    // old/hand-typed link) still displays as selected — the backend already
    // narrows case-insensitively, so the display should agree with it rather
    // than rendering blank while still silently filtering correctly. Falls
    // back to `null` (never the raw string) when there's no match — this
    // Autocomplete isn't `freeSolo`, so `value` must always be either `null`
    // or one of `options`; the stale-selection effect above keeps
    // `selectedSubTeamSearch` in sync with `subTeamOptions` so this should
    // only miss transiently, but `null` is the safe fallback either way.
    const matchedSubTeamOption = subTeamOptions?.find(
      o => o.toLowerCase() === selectedSubTeamSearch.trim().toLowerCase(),
    );

    const lockedTeamCell = subTeamSearchEnabled ? (
      <Grid item xs={12} md={3}>
        <Autocomplete
          fullWidth
          size="small"
          options={subTeamOptions ?? []}
          value={matchedSubTeamOption ?? null}
          onChange={(_e, newValue) => setSelectedSubTeamSearch(newValue ?? '')}
          renderInput={params => (
            <TextField
              {...params}
              variant="outlined"
              label="Sub-team"
              placeholder="All sub-teams"
            />
          )}
        />
      </Grid>
    ) : (
      <Grid item xs={12} md={3}>
        <Typography variant="body2">Filtered by team: {lockedTeam}</Typography>
      </Grid>
    );

    // Same control family as Team: searchable, and can't be cleared to empty —
    // deleting the text and pressing Enter falls back to 'All'.
    const systemInstanceCell = (
      <Grid item xs={12} md={3}>
        <Autocomplete
          fullWidth
          size="small"
          disableClearable
          options={systemInstanceChoices}
          value={selectedSystemInstanceOption}
          getOptionLabel={o => o.label}
          getOptionSelected={(o, v) => o.value === v.value}
          filterOptions={filterSystemInstanceOptions}
          onChange={(_e, option) =>
            handleSystemInstanceChange(option ?? ALL_SYSTEMS_OPTION)
          }
          renderOption={(option, { inputValue }) => (
            <span style={systemInstanceRowStyle(option.level)}>
              {/* While filtering, a row can match without the System header
                  above it, and two instances may share a name — so each says
                  which System it is under. Unfiltered (or showing the chosen
                  label), the hierarchy already says it. */}
              {option.level === 'instance' &&
              inputValue.trim() !== '' &&
              inputValue !== selectedSystemInstanceOption.label
                ? option.label
                : option.name}
            </span>
          )}
          renderInput={params => (
            <TextField
              {...params}
              variant="outlined"
              label="System / Deployed Instance"
              onKeyDown={e => {
                if (
                  e.key === 'Enter' &&
                  (e.target as HTMLInputElement).value.trim() === ''
                ) {
                  handleSystemInstanceChange(ALL_SYSTEMS_OPTION);
                }
              }}
            />
          )}
        />
      </Grid>
    );

    return (
      <Grid container spacing={2}>
        {lockedTeam ? (
          lockedTeamCell
        ) : (
          <>
            <Grid item xs={12} md={3}>
              <FormControl fullWidth variant="outlined" size="small">
                <InputLabel>Experience</InputLabel>
                <Select
                  value={displayedExperience}
                  onChange={e => {
                    setSelectedExperience(e.target.value as string);
                    setSelectedTeam('All');
                    teamOverride.dismiss();
                  }}
                  label="Experience"
                >
                  <MenuItem value="All">All</MenuItem>
                  <MenuItem value="Unknown">Unknown</MenuItem>
                  {experiences.map(exp => (
                    <MenuItem key={exp} value={exp}>
                      {exp}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={3}>
              <Autocomplete
                fullWidth
                size="small"
                disableClearable
                options={
                  displayOverride?.team &&
                  !availableTeams.includes(displayOverride.team)
                    ? ['All', displayOverride.team, ...availableTeams]
                    : ['All', ...availableTeams]
                }
                value={displayedTeam}
                onChange={(_e, newValue) => {
                  setSelectedTeam(newValue ?? 'All');
                  teamOverride.dismiss();
                }}
                renderInput={params => (
                  <TextField
                    {...params}
                    variant="outlined"
                    label="Team"
                    onKeyDown={e => {
                      // Deleting the text and hitting Enter resets to the
                      // default ('All') rather than requiring the option to be
                      // explicitly re-selected from the dropdown.
                      if (
                        e.key === 'Enter' &&
                        (e.target as HTMLInputElement).value.trim() === ''
                      ) {
                        setSelectedTeam('All');
                        teamOverride.dismiss();
                      }
                    }}
                  />
                )}
              />
            </Grid>
          </>
        )}
        {showSystemFilters && systemInstanceCell}
        {/* One control, two levels. Pillars are selectable in their own right;
          their categories are indented beneath them as sub-selections. A
          pillar whose only category repeats its own name (Vulnerability,
          Attack Paths) renders just the pillar row — the sub-row would be a
          duplicate that does the same thing. Hidden entirely when the
          vocabulary failed to load rather than showing an empty dropdown. */}
        {showTaxonomyFilter && pillars.length > 0 && (
          <Grid item xs={12} md={3}>
            <FormControl fullWidth variant="outlined" size="small">
              <InputLabel id="taxonomy-filter-label">
                Pillar / Category
              </InputLabel>
              <Select
                labelId="taxonomy-filter-label"
                value={displayedTaxonomySelection}
                onChange={e => {
                  handleTaxonomyChange(e.target.value as string);
                  taxonomyOverride.dismiss();
                }}
                label="Pillar / Category"
              >
                <MenuItem value="All">All</MenuItem>
                <MenuItem value={UNCLASSIFIED_TAXONOMY}>Unclassified</MenuItem>
                {pillars.flatMap(p => {
                  const own = categories.filter(c => c.pillar_code === p.code);
                  const rows = [
                    <MenuItem
                      key={`p:${p.code}`}
                      value={`p:${p.code}`}
                      style={{ fontWeight: 600 }}
                    >
                      {p.label}
                    </MenuItem>,
                  ];
                  if (own.length === 1 && own[0].label === p.label) return rows;
                  return rows.concat(
                    own.map(c => (
                      <MenuItem
                        key={`c:${c.code}`}
                        value={`c:${c.code}`}
                        style={{ paddingLeft: 34 }}
                      >
                        {c.label}
                      </MenuItem>
                    )),
                  );
                })}
              </Select>
            </FormControl>
          </Grid>
        )}
        <Grid item xs={12} md={3}>
          <FormControl fullWidth variant="outlined" size="small">
            <InputLabel>Tool</InputLabel>
            <Select
              value={displayedIssueType}
              onChange={e => {
                setSelectedIssueType(e.target.value as string);
                issueTypeOverride.dismiss();
              }}
              label="Tool"
            >
              <MenuItem value="All">All</MenuItem>
              {issueTypes.map(t => (
                <MenuItem key={t} value={t}>
                  {formatToolName(t)}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>
        <Grid item xs={12} md={3}>
          <TextField
            fullWidth
            variant="outlined"
            size="small"
            label={searchLabel}
            placeholder={searchPlaceholder}
            value={selectedCve}
            onChange={e => setSelectedCve(e.target.value)}
          />
        </Grid>
        <Grid item xs={12} md={3}>
          <FormControlLabel
            control={
              <Checkbox
                checked={selectedSloCheck}
                onChange={e => setSelectedSloCheck(e.target.checked)}
              />
            }
            label="Out of SLO only"
          />
        </Grid>
        <Grid item xs={12} md={3}>
          <Tooltip title="When enabled, counts unique findings per asset + CVE/rule instead of counting each tool report separately">
            <FormControlLabel
              control={
                <Switch
                  checked={deduplicate}
                  onChange={e => setDeduplicate(e.target.checked)}
                  size="small"
                />
              }
              label="Unique findings only"
            />
          </Tooltip>
        </Grid>
        <Grid item xs={12} md={3}>
          <Tooltip title="When enabled, includes findings that have been quiesced (temporarily suppressed)">
            <FormControlLabel
              control={
                <Checkbox
                  checked={includeQuiesced}
                  onChange={e => setIncludeQuiesced(e.target.checked)}
                />
              }
              label="Show quiesced"
            />
          </Tooltip>
        </Grid>
        <Grid item xs={12}>
          <Box
            display="flex"
            alignItems="center"
            flexWrap="wrap"
            style={{ gap: 4 }}
          >
            {severities.map(sev => (
              <Chip
                key={sev}
                label={sev}
                clickable
                onClick={() => toggleSeverity(sev)}
                style={{
                  backgroundColor: selectedSeverities.includes(sev)
                    ? SEVERITY_COLORS[sev] || '#757575'
                    : undefined,
                  color: selectedSeverities.includes(sev) ? '#fff' : undefined,
                }}
                variant={
                  selectedSeverities.includes(sev) ? 'default' : 'outlined'
                }
              />
            ))}
            <Button
              size="small"
              style={{ textTransform: 'none' }}
              disabled={
                severities.length === 0 ||
                selectedSeverities.length === severities.length
              }
              onClick={() => setSelectedSeverities(sortSeverities(severities))}
            >
              All
            </Button>
            <Button
              size="small"
              style={{ textTransform: 'none' }}
              disabled={
                selectedSeverities.length === defaultSeveritiesFromApi.length &&
                selectedSeverities.every(s =>
                  defaultSeveritiesFromApi.includes(s),
                )
              }
              onClick={() => setSelectedSeverities(defaultSeveritiesFromApi)}
            >
              Default
            </Button>
          </Box>
        </Grid>
      </Grid>
    );
  },
);

interface ReportFilterPanelProps {
  onFiltersChange: (filters: {
    selectedOrg: string;
    selectedExperience: string;
    selectedTeam: string;
    selectedSeverities: string[];
    selectedFirstDate: string;
    selectedLastDate: string;
    excludedRepos: string[];
    selectedToolNames: string[];
  }) => void;
}

export const ReportFilterPanel = ({
  onFiltersChange,
}: ReportFilterPanelProps) => {
  const api = useApi(securityInsightsApiRef);
  const [searchParams, setSearchParams] = useSearchParams();
  const initialized = useRef(false);

  const [experiences, setExperiences] = useState<string[]>([]);
  const [teams, setTeams] = useState<string[]>([]);
  const [teamsMap, setTeamsMap] = useState<Record<string, string[]>>({});
  const [orgs, setOrgs] = useState<string[]>([]);

  // Default to the past 30 days (today inclusive) so the report always reflects
  // the most recent ingest, not a calendar-month window that goes stale as soon
  // as the current month begins.
  //
  // Format using LOCAL date components, not toISOString (which is UTC). For a
  // user in UTC-5 at 9pm local on June 4, `today.toISOString()` returns the
  // June 5 UTC date — making the default "today" one day ahead of what the
  // user sees on their calendar. `<input type="date">` displays / accepts
  // local calendar days, so formatting must match.
  const formatLocalDate = (d: Date) =>
    `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
  const today = new Date();
  // Calendar arithmetic, not millisecond subtraction: `30 * 86400000` drifts
  // by an hour across DST transitions and can flip the local day boundary,
  // so the default "30 days ago" would land on day 29 (or day 31) for users
  // crossing a DST switch in the window. setDate handles month/year carry.
  const thirtyDaysAgo = new Date(today);
  thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);
  const defaultFirstDate = formatLocalDate(thirtyDaysAgo);
  const defaultLastDate = formatLocalDate(today);

  const [selectedExperience, setSelectedExperience] = useState(
    searchParams.get('experience') || 'All',
  );
  const [selectedTeam, setSelectedTeam] = useState(
    searchParams.get('team') || 'All',
  );
  const [selectedOrg, setSelectedOrg] = useState(
    searchParams.get('org') || 'All',
  );
  const [selectedSeverities] = useState([
    'critical',
    'high',
    'medium',
    'low',
    'unknown',
  ]);
  const [selectedFirstDate, setSelectedFirstDate] = useState(
    searchParams.get('firstDate') || defaultFirstDate,
  );
  const [selectedLastDate, setSelectedLastDate] = useState(
    searchParams.get('lastDate') || defaultLastDate,
  );

  useEffect(() => {
    Promise.all([
      api.getExperiences(),
      api.getTeams(),
      api.getTeamsInExperiences(),
      api.getOrgs(),
    ]).then(([exps, tms, tMap, os]) => {
      setExperiences(exps);
      setTeams(tms);
      setTeamsMap(tMap);
      setOrgs(os);
      initialized.current = true;
    });
  }, [api]);

  // Sync filters to URL (only non-default values)
  useEffect(() => {
    if (!initialized.current) return;
    setSearchParams(
      prev => {
        const params = new URLSearchParams(prev);
        const filterKeys = [
          'experience',
          'team',
          'org',
          'firstDate',
          'lastDate',
        ];
        filterKeys.forEach(k => params.delete(k));
        if (selectedExperience !== 'All')
          params.set('experience', selectedExperience);
        if (selectedTeam !== 'All') params.set('team', selectedTeam);
        if (selectedOrg !== 'All') params.set('org', selectedOrg);
        if (selectedFirstDate !== defaultFirstDate)
          params.set('firstDate', selectedFirstDate);
        if (selectedLastDate !== defaultLastDate)
          params.set('lastDate', selectedLastDate);
        return params;
      },
      { replace: true },
    );
  }, [
    selectedExperience,
    selectedTeam,
    selectedOrg,
    selectedFirstDate,
    selectedLastDate,
    defaultFirstDate,
    defaultLastDate,
    setSearchParams,
  ]);

  // Emit filter changes immediately. The page-level data-fetch effect in
  // `SecurityReportsPage` already debounces by 300ms when `currentFilters`
  // updates; adding another panel-level debounce here would stack a second
  // 300ms (~600ms total) onto every filter change.
  //
  // Skip the emit entirely when either date is empty. `<input type="date">`
  // can momentarily report an empty value while the user is mid-edit, and
  // the backend renders these into `DATE('')` which Postgres rejects with a
  // 500 ("invalid input syntax for date"). Holding back the request keeps
  // the prior data on screen until both dates are valid again.
  useEffect(() => {
    if (!selectedFirstDate || !selectedLastDate) return;
    onFiltersChange({
      selectedOrg,
      selectedExperience,
      selectedTeam,
      selectedSeverities,
      selectedFirstDate,
      selectedLastDate,
      excludedRepos: [],
      selectedToolNames: [],
    });
  }, [
    selectedOrg,
    selectedExperience,
    selectedTeam,
    selectedSeverities,
    selectedFirstDate,
    selectedLastDate,
    onFiltersChange,
  ]);

  const availableTeams =
    selectedExperience !== 'All' && selectedExperience !== 'Unknown'
      ? teamsMap[selectedExperience] || []
      : teams;

  return (
    <Grid container spacing={2}>
      <Grid item xs={12} md={4}>
        <FormControl fullWidth variant="outlined" size="small">
          <InputLabel>Experience</InputLabel>
          <Select
            value={selectedExperience}
            onChange={e => {
              setSelectedExperience(e.target.value as string);
              setSelectedTeam('All');
            }}
            label="Experience"
          >
            <MenuItem value="All">All</MenuItem>
            {experiences.map(exp => (
              <MenuItem key={exp} value={exp}>
                {exp}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Grid>
      <Grid item xs={12} md={4}>
        <FormControl fullWidth variant="outlined" size="small">
          <InputLabel>Team</InputLabel>
          <Select
            value={selectedTeam}
            onChange={e => setSelectedTeam(e.target.value as string)}
            label="Team"
          >
            <MenuItem value="All">All</MenuItem>
            {availableTeams.map(t => (
              <MenuItem key={t} value={t}>
                {t}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Grid>
      <Grid item xs={12} md={4}>
        <FormControl fullWidth variant="outlined" size="small">
          <InputLabel>Organization</InputLabel>
          <Select
            value={selectedOrg}
            onChange={e => setSelectedOrg(e.target.value as string)}
            label="Organization"
          >
            <MenuItem value="All">All</MenuItem>
            {orgs.map(o => (
              <MenuItem key={o} value={o}>
                {o}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Grid>
      <Grid item xs={12} md={3}>
        <TextField
          fullWidth
          variant="outlined"
          size="small"
          label="From"
          type="date"
          value={selectedFirstDate}
          onChange={e => setSelectedFirstDate(e.target.value)}
          InputLabelProps={{ shrink: true }}
        />
      </Grid>
      <Grid item xs={12} md={3}>
        <TextField
          fullWidth
          variant="outlined"
          size="small"
          label="To"
          type="date"
          value={selectedLastDate}
          onChange={e => setSelectedLastDate(e.target.value)}
          InputLabelProps={{ shrink: true }}
        />
      </Grid>
    </Grid>
  );
};
